/**
 * The air the aeroplane is flying through.
 *
 * WHY THIS EXISTS
 * A commanded path is a mathematical curve, and a mathematical curve is the one
 * thing a real aeroplane never flies. Everything else in this trainer can be
 * exactly right — the bank angle, the turn radius, the descent rate, the sight
 * picture — and the result still reads as a model on a wire, because nothing in
 * it ever varies. The eye is extremely good at spotting that. It is the same
 * reason a perfectly steady camera move looks computer-generated.
 *
 * So: light turbulence. Not enough to fly through, which would defeat the point
 * of a trainer where somebody else is doing the flying, but enough that the
 * wings are never quite level for two seconds together and the aeroplane is
 * visibly being HELD on the path rather than following one.
 *
 * IT IS NOT RANDOM
 * Every number here comes out of a sum of sines with mutually irrational
 * periods. That gives motion which never repeats over the length of a pattern
 * but is perfectly reproducible run to run — which matters more than it sounds,
 * because the entire test suite flies this pattern and asserts where the hook
 * lands. Turbulence from `Math.random()` would mean a build that fails one time
 * in fifty and nobody able to reproduce it.
 */

import { clamp } from '../util/units.js'

/**
 * Sum of sines at incommensurate periods. Three components is enough to look
 * unpredictable and cheap enough to call several times a frame.
 *
 * @param {number} t seconds
 * @param {number} seed shifts the phase so each axis gets its own motion
 */
function wander(t, seed) {
  return (
    0.55 * Math.sin(t * 0.7331 + seed) +
    0.30 * Math.sin(t * 1.7913 + seed * 2.3) +
    0.15 * Math.sin(t * 3.3701 + seed * 5.7)
  )
}

/**
 * How strong the air is, as a fraction, given where you are in the pattern.
 *
 * Two shaping rules, both of them things a carrier pilot would recognise:
 *
 *   It eases off in close. Not because the air smooths out — it does the
 *   opposite — but because this trainer grades you on where the hook lands, and
 *   an aeroplane being shoved around by scripted turbulence in the last two
 *   hundred feet is being graded on the turbulence. The correction is the
 *   pilot's job in a flying simulator; here it is noise on somebody else's
 *   instrument.
 *
 *   It picks up in the burble. The air behind a carrier is genuinely disturbed
 *   — the island and the hull leave a wake the aeroplane flies through from
 *   about a quarter mile in, and the classic signature is a sink followed by a
 *   push right at the ramp. It is one of the things that makes the last few
 *   seconds of a carrier approach hard, so a trainer that shows a glassy
 *   approach is teaching the wrong expectation.
 *
 * @param {number} toTouchdownFt range to the touchdown point
 * @param {boolean} onGlidepath
 */
export function turbulenceScale(toTouchdownFt, onGlidepath, onFinal) {
  // The range shaping only means anything ON FINAL. Range to the touchdown
  // point is a number everywhere in the pattern, including three miles astern
  // on the initial and abeam the island at 800 ft — and keying the fade on it
  // regardless had the aeroplane fly through its own landing burble at the
  // break, fifty feet up the wrong side of the ship.
  if (!onFinal) return 1
  // Fade out over the last nine hundred feet, not the last two hundred.
  //
  // The ball is an ANGLE — 0.7 degrees is full scale — so its sensitivity to a
  // few feet of height goes up as range comes down. Half a mile out, full scale
  // is thirty-seven feet and a gust is invisible on the lens. At the ramp it is
  // under four feet, and the same gust pegs it. So the fade has to be finished
  // well before the aeroplane gets to where the ball is that twitchy, which
  // conveniently is also where the real burble has already done its work.
  const closeFade = clamp((toTouchdownFt - 150) / 750, 0, 1)
  // The nearer the ramp, the gentler: the last part of the approach is the
  // graded part, so the ball should move without ever wandering off the lens.
  //
  // Ramped on RANGE rather than switched on the glidepath latch. The latch is a
  // boolean that flips between two frames, and halving the gust strength across
  // one frame is a 2,900 fpm/s step in vertical speed — a disturbance model
  // that introduces a bigger discontinuity than the one it is smoothing over.
  const onSlope = 0.34 + 0.66 * clamp((toTouchdownFt - 2500) / 3000, 0, 1)
  void onGlidepath
  return closeFade * onSlope
}

/**
 * The burble, as a vertical gust in feet per minute.
 *
 * Positive is up. The shape is the standard one: a region of sink from about
 * 1,200 ft in, deepening to a minimum around 600 ft, then a push up over the
 * round-down. Modelled as a single smooth wavelet rather than a lookup table,
 * because the shape is the teaching point and the exact magnitudes vary with
 * every ship, every wind and every day.
 */
export function burbleFpm(toTouchdownFt, strength = 1, onFinal = true) {
  if (!onFinal || toTouchdownFt > 1400 || toTouchdownFt < 0) return 0
  // x runs 0 at the touchdown point to 1 at the edge of the burble.
  const x = toTouchdownFt / 1400
  // A lobe below the axis in the middle distance and a smaller one above it
  // close in. sin(2 pi x) * a decaying envelope does exactly that.
  const envelope = Math.exp(-3.2 * x) * (1 - x)
  // PEAK MAGNITUDE MATTERS. The first version peaked at 900 fpm, which sounds
  // like a real burble and is not one the trainer can survive: on the glidepath
  // the altitude trim is deliberately clamped to 320 fpm so it cannot hunt
  // against the feed-forward, so a 900 fpm push is three times the authority
  // available to reject it. The aeroplane arrived at the ramp fifty feet below
  // the sea. A disturbance has to be small enough that the loop rejecting it
  // actually can.
  return -Math.sin(x * Math.PI * 1.9) * envelope * BURBLE_PEAK_FPM * strength
}

/** Peak vertical gust in the burble. Sized against the controller's authority. */
export const BURBLE_PEAK_FPM = 150

/**
 * Light turbulence, as offsets to add to what the guidance commanded.
 *
 * @param {number} t seconds since the pattern started
 * @param {number} scale 0 to 1, from `turbulenceScale`
 * @param {number} intensity the environment's turbulence setting
 */
export function gust(t, scale, intensity = 1) {
  const k = scale * intensity
  return {
    /** Degrees of bank the air puts in that the pilot has to take out. */
    bankDeg: wander(t, 0.0) * 1.5 * k,
    /** Vertical gust, feet per minute. */
    vsFpm: wander(t, 2.1) * 90 * k,
    /** Airspeed nibble, knots. */
    ktas: wander(t, 4.7) * 1.6 * k,
    /** Yaw, degrees — small, and mostly what makes the nose feel alive. */
    yawDeg: wander(t, 6.3) * 0.7 * k,
  }
}
